
import router from "@/router";
import { Chapters, MMIUser, mmiStructurePath } from "@/store/MMI/MMIInterfaces";
import { defineComponent } from "vue";
import { mapGetters } from "vuex";
import { doc, getDoc } from "firebase/firestore";
import * as fb from "../../firebase";
import MmiUserCard from "./UserCard.vue";
import MmiCurrentUserCard from "./CurrentUserCard.vue";
import MmiStructureSkeleton from "./StructureSkeleton.vue";
import { Searching, UserNotFound } from "@/components/search-users/";
import Register from "./Register.vue";
import VideosPlaylist from "./VideosPlaylist.vue";
import { SubscriptionInfo } from "functions/src/Subscriptions/SubscriptionsInterfaces";
import { VideoData } from "functions/src/MMI/MMIInterfaces";
import { ClassModal } from "../schedule";

// La pantalla del Acelerador de Riqueza vive en una ruta PLANA desde que se
// aplanó `/mastermind-intensive/colombia`: el capítulo ya no viaja en la URL.
// `ACELERADOR_ROUTE` es el `name` de la ruta en `router.ts` y tiene que ser
// idéntico al `Key` del documento `menu/acelerador` de Firestore, porque
// `stores/menu.ts` empareja el ítem de menú con la ruta comparando esos dos.
const ACELERADOR_ROUTE = "acelerador";
const ACELERADOR_PATH = "/" + ACELERADOR_ROUTE;

/**
 * Si esta persona está o no dentro de la estructura del Acelerador.
 *
 * - `unknown`: todavía se está comprobando (una lectura de Firestore).
 * - `member`: está dentro; hay estructura que pedir y esperar.
 * - `outsider`: no está; se le pinta la cara de compra sin llamar a nadie.
 */
type MmiMembership = "unknown" | "member" | "outsider";

export default defineComponent({
  name: "mmi-colombia",
  components: {
    Register,
    UserNotFound,
    Searching,
    MmiUserCard,
    MmiCurrentUserCard,
    MmiStructureSkeleton,
    VideosPlaylist,
    ClassModal,
  },
  data() {
    return {
      isActive: false,
      // Tipado como `Chapters` (y no como `string` a secas) para que
      // `mmiStructurePath()` lo acepte sin conversiones en cada sitio.
      chapter: "colombia" as Chapters,
      loading: true,
      refreshing: false,
      displayedUser: "",
      searchUsername: "",
      searching: false,
      userNotFound: false,
      currentVideo: {} as VideoData,
      // Ver el tipo `MmiMembership` arriba y `isStructureMember()` abajo.
      membership: "unknown" as MmiMembership,
      // Clave («capítulo/usuario») de la petición de estructura que está en
      // vuelo, o cadena vacía si no hay ninguna.
      //
      // POR QUÉ HACE FALTA: entrar a la pantalla dispara el watcher de `$route`
      // DOS veces —la primera con la URL todavía sin `_u`, y la segunda con el
      // `_u` que le pone `goRoot()` desde `mounted()`—, y las dos apuntan al
      // mismo usuario. Sin esta marca se lanzaban DOS llamadas simultáneas a
      // `MMI-getUserStructure` para pintar exactamente lo mismo, o sea dos
      // arranques en frío de Cloud Functions en vez de uno.
      pendingStructureRequest: "",
      // Los vídeos del capítulo se piden una sola vez por montaje.
      chapterMediaRequested: false,
    };
  },
  computed: {
    ...mapGetters("mmi", {
      getUserStructure: "getUserStructure",
      getChapterVideosPlaylist: "getChapterVideosPlaylist",
      getWatchedVideos: "getWatchedVideos",
    }),
    ...mapGetters("auth", {
      getUsernamePK: "getUsernamePK",
      getUserRole: "getUserRole",
    }),
    ...mapGetters("subs", ["getSubscriptionConfig", "getUserSubscription"]),
    // Puede valer `undefined`: la configuración del catálogo llega por un
    // listener de Firestore y en una recarga directa sobre `/acelerador` el
    // componente se monta ANTES de que aterrice. El tipo lo dice ahora
    // explícitamente porque el `mounted()` de antes hacía
    // `this.SubscriptionConfig.videos` y en ese caso reventaba en silencio.
    SubscriptionConfig(): SubscriptionInfo | undefined {
      return this.getSubscriptionConfig("mmi-colombia");
    },
    currentStructure(): MMIUser {
      return this.getUserStructure(this.chapter, this.displayedUser);
    },
    breakRowAt(): { 2: number; 3: number; 5: number } {
      return {
        2: this.currentStructure.users.length % 2,
        3: this.currentStructure.users.length % 3,
        5: this.currentStructure.users.length % 5,
      };
    },
    isRootNode(): boolean {
      return this.getUsernamePK === this.displayedUser;
    },
    displayUser(): boolean {
      return this.currentStructure && !this.userNotFound && !this.searching;
    },
    playlist(): Array<VideoData> {
      return this.getChapterVideosPlaylist(this.chapter);
    },
    watchedVideos(): Array<string> {
      return this.getWatchedVideos(this.chapter);
    },
    editPermision() {
      const roles = this.$store.getters["auth/getUserRole"];
      return (
        Array.isArray(roles) &&
        roles.some((e) => ["master", "root"].includes(e))
      );
    },
  },
  methods: {
    /**
     * ¿Está esta persona DENTRO de la estructura del Acelerador?
     *
     * Se le pregunta al mismo documento que consulta `MMI-getUserStructure`
     * antes que a nada: `mmiTree/{capítulo}/structure/{usuario}` (ver
     * `functions/src/MMI/MMIFunc.ts`, donde un `!user.exists` es exactamente el
     * error `USER_NOT_FOUND` que hace caer la pantalla en la cara de compra).
     * Al preguntar por el MISMO dato, la respuesta no puede discrepar de la de
     * la callable — pero cuesta una lectura suelta de Firestore, milisegundos,
     * en vez de una Cloud Function con `minInstances: 0`, o sea un arranque en
     * frío de varios segundos.
     *
     * OJO CON EL ÁRBOL: son DOS colecciones. La vieja, `mmi/{capítulo}/
     * structure`, quedó congelada en el reinicio de red; la viva es
     * `mmiTree/{capítulo}/structure`, donde coloca `PlaceMmiMember.ts` y de
     * donde lee la callable. Esta comprobación apuntaba a la vieja, y como los
     * renombrados del reinicio (`gtr.academy` → `gtr.master`, `laurapelayo` →
     * `patybrambila`) sólo se aplicaron a la nueva, a los renombrados y a
     * cualquier alta posterior al corte se les pintaba la cara de compra
     * teniendo `mmi-colombia` vigente. El detalle completo, en
     * `mmiStructurePath()` (`store/MMI/MMIInterfaces.ts`), que es de donde sale
     * la ruta para que no vuelva a haber dos versiones de ella.
     *
     * Y ese documento no puede existir sin el producto: la colocación crea el
     * nodo y la suscripción `mmi-{capítulo}` en la MISMA transacción
     * (`PlaceMmiMember.ts`).
     *
     * Las reglas permiten esta lectura sólo sobre el documento PROPIO
     * (`firebase-config/firestore.rules`:
     * `match /mmiTree/colombia/structure/{username}` con `isOwner`), que es
     * justo el caso donde se usa. Para navegar por la estructura de otra
     * persona hace falta la callable de todas formas, porque ahí además se
     * comprueba que sea de tu red.
     */
    async isStructureMember(username: string): Promise<boolean> {
      const node = doc(
        fb.firestore,
        ...mmiStructurePath(this.chapter),
        username
      );
      return await getDoc(node)
        .then((snapshot) => snapshot.exists())
        .catch((error) => {
          // Si la lectura falla (reglas, red) NO se concluye que no tiene el
          // producto: se responde «sí» y decide la callable, que es el
          // comportamiento de toda la vida. Un falso «no» le enseñaría la
          // pantalla de compra a alguien que ya compró, y eso es mucho peor que
          // esperar.
          console.error(
            "No se pudo comprobar la membresía del Acelerador",
            error
          );
          return true;
        });
    },
    /**
     * Los vídeos del capítulo y la lista de «ya vistos».
     *
     * Vivían en `mounted()` y se pedían siempre. Ahora sólo se piden cuando ya
     * se sabe que la persona está en la estructura, porque la lista de
     * reproducción sale de OTRA callable —`MMI-getChapterVideosPlaylist`, con
     * su propio arranque en frío— y sólo se pinta en la cara del socio con
     * CMMI. A quien no lo tiene no se le enseña por ningún lado: pedirla era
     * pagar una segunda espera para tirar el resultado a la basura.
     */
    loadChapterMedia() {
      if (this.chapterMediaRequested) {
        return;
      }
      this.chapterMediaRequested = true;
      this.$store
        .dispatch("mmi/doGetChapterVideosPlaylist", this.chapter)
        .catch((error) => {
          console.error("Error cargando la lista de vídeos: ", error);
        });
      this.$store.dispatch("mmi/doGetWatchedVideos", this.chapter);
    },
    /**
     * El vídeo que suena por defecto (el «content» del catálogo).
     *
     * Se sacó de `mounted()` porque la configuración del catálogo llega por un
     * listener de Firestore que puede aterrizar DESPUÉS del montaje. Antes eso
     * dejaba el reproductor sin `src` para siempre; ahora el watcher de
     * `SubscriptionConfig` vuelve a pasar por aquí en cuanto llega, y sólo
     * rellena si el socio no ha elegido ya otro vídeo de la lista.
     */
    applyDefaultVideo() {
      if (this.currentVideo.url) {
        return;
      }
      this.currentVideo.url = this.SubscriptionConfig?.videos?.content ?? "";
    },
    /**
     * Decide qué hay que cargar para el usuario que pide la URL.
     *
     * El orden es deliberado: primero lo que ya está en memoria, luego la
     * comprobación barata, y sólo al final la callable cara.
     */
    async loadDisplayedUser() {
      const username = this.displayedUser;

      // Ya se pidió esta estructura antes en esta sesión: ni llamada ni espera.
      if (this.getUserStructure(this.chapter, username) !== undefined) {
        this.membership = "member";
        this.isActive = true;
        this.loading = false;
        this.loadChapterMedia();
        return;
      }

      const request = this.chapter + "/" + username;
      if (this.pendingStructureRequest === request) {
        return;
      }
      this.pendingStructureRequest = request;
      this.loading = true;

      try {
        // Sólo el nodo PROPIO se comprueba por Firestore; el de otra persona ya
        // implica que quien mira está dentro (ha llegado navegando o buscando),
        // y además las reglas no le dejarían leerlo.
        if (username === this.getUsernamePK) {
          const isMember = await this.isStructureMember(username);
          this.membership = isMember ? "member" : "outsider";
          if (!isMember) {
            // AQUÍ ESTÁ EL AHORRO: sin CMMI, la pantalla que se va a ver es la
            // de compra, y ésa no necesita la estructura para nada. Nos
            // saltamos la callable entera y con ella su arranque en frío.
            this.isActive = false;
            return;
          }
        } else {
          this.membership = "member";
        }

        this.loadChapterMedia();
        await this.$store
          .dispatch("mmi/doGetUserStructure", {
            chapter: this.chapter,
            username: username,
          })
          .then(() => {
            this.isActive = true;
          })
          .catch((error) => {
            if (
              !(
                error instanceof Error &&
                (error.name === "USER_NOT_FOUND" ||
                  error.name === "USER_NOT_AUTHORIZED")
              )
            ) {
              console.error("Error: ", error);
              alert(error);
            }
          });
      } finally {
        this.pendingStructureRequest = "";
        this.loading = false;
      }
    },
    async searchUser() {
      this.userNotFound = false;
      if (
        this.getUserStructure(this.chapter, this.searchUsername) === undefined
      ) {
        this.searching = true;
        await this.$store
          .dispatch("mmi/doGetUserStructure", {
            chapter: this.chapter,
            username: this.searchUsername,
          })
          .then(() => {
            router.push({
              path: ACELERADOR_PATH,
              query: { _u: this.searchUsername },
            });
          })
          .catch((error) => {
            if (error instanceof Error) {
              if (
                error.name === "USER_NOT_FOUND" ||
                error.name === "USER_NOT_AUTHORIZED"
              ) {
                this.userNotFound = true;
              } else {
                alert(error.message);
              }
            }
          })
          .finally(() => {
            this.searching = false;
          });
      } else {
        router.push({
          path: ACELERADOR_PATH,
          query: { _u: this.searchUsername },
        });
      }
    },
    goRoot() {
      this.userNotFound = false;
      router.push({
        path: ACELERADOR_PATH,
        query: { _u: this.getUsernamePK },
      });
    },
    goUp() {
      this.userNotFound = false;
      router.push({
        path: ACELERADOR_PATH,
        query: { _u: this.currentStructure.parent },
      });
    },
    async refresh() {
      this.refreshing = true;
      await this.$store
        .dispatch("mmi/doGetUserStructure", {
          chapter: this.chapter,
          username: this.displayedUser,
        })
        .catch((err) => {
          console.error(err);
          alert(err);
        })
        .finally(() => {
          this.refreshing = false;
        });
    },
    changeVideo(videoData: VideoData) {
      this.currentVideo = videoData;
    },
    addWatchedVideo(videoId: string) {
      this.$store.dispatch("mmi/addWatchedVideo", {
        chapter: this.chapter,
        videoId: videoId,
      });
    },
    removeWatchedVideo(videoId: string) {
      this.$store.dispatch("mmi/removeWatchedVideo", {
        chapter: this.chapter,
        videoId: videoId,
      });
    },
  },
  mounted() {
    if (this.$route.query._u === undefined) {
      this.goRoot();
    }
    this.applyDefaultVideo();
    // La lista de vídeos ya NO se pide aquí: la pide `loadDisplayedUser()`
    // cuando confirma que hay un socio al que enseñársela.
  },
  unmounted() {
    this.$store.dispatch("mmi/doClearWatchedVideosListener", this.chapter);
  },
  watch: {
    $route: {
      handler() {
        if (this.$route.name !== ACELERADOR_ROUTE) {
          return;
        }
        this.displayedUser = this.$route.query._u
          ? this.$route.query._u
          : this.getUsernamePK;
        this.loadDisplayedUser();
      },
      immediate: true,
    },
    // El catálogo puede llegar después del montaje (ver `applyDefaultVideo`).
    SubscriptionConfig() {
      this.applyDefaultVideo();
    },
  },
});
